import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import {
  UntypedFormControl,
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  Subscription,
  Subject,
  Observable,
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  tap,
} from 'rxjs';
import { AutofillMonitor, AutofillEvent } from '@angular/cdk/text-field';
import { popsyCheckPhone, popsyParseAnyPhone } from '@app/shared/utils/validation.utils';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import {
  findPhoneCountryInfo,
  findPhoneCountryInfoWithCountryPrefix,
  PhoneCountryInfo,
} from '@app/shared/data/countries.data';

import examplePhoneNumbers from 'libphonenumber-js/examples.mobile.json';
import { AsYouType, CountryCode, getExampleNumber } from 'libphonenumber-js';

import { PhoneCountryOverlayDynamicService } from '@app/overlays/phone-country-overlay/services/phone-country-overlay-dynamic.service';
import { NgIf, AsyncPipe } from '@angular/common';
import { PopsySkeletonComponent } from '@app/components/skeleton/popsy-skeleton.component';
import { A11yModule } from '@angular/cdk/a11y';

const MIN_LENGTH = 8;

const DEBOUNCE_USER_INPUT = 300;

const PHONE_COUNTRY_CODE_MIN_LENGTH = 2;
const PHONE_COUNTRY_CODE_MAX_LENGTH = 4;

const LIMIT_PHONE_COUNTRIES = [
  'BR',
  'SA',
  'AE',
  'QA',
  'OM',
  'KW',
  'BH',
  'TR',
  'EG',
  'LB',
  'JO',
  'IL',
  'CL',
  'CO',
  'CR',
  'MX',
  'CA',
  'PE',
  'US',
  'CA',
  'ES',
  'PT',
];

export interface PhoneMetadata {
  countryCode: string;
  areaCode: string;
  national: string;
  display: string;
}

export const parsePhoneMetadata = (data?: any | null): PhoneMetadata => {
  const out: PhoneMetadata = {
    countryCode: '',
    areaCode: '',
    national: '',
    display: '',
  };
  if (data) {
    out.countryCode = data.country_code || data.countryCode || '';
    out.countryCode = data.area_code || data.areaCode || '';
    out.countryCode = data.national || '';
    out.countryCode = data.display || '';
  }
  return out;
};

@Component({
  selector: 'app-stc-phone-input',
  templateUrl: './stc-phone-input.template.html',
  styleUrls: ['./stc-phone-input.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    NgIf,
    ReactiveFormsModule,
    PopsySkeletonComponent,
    AsyncPipe,
    A11yModule,
  ],
})
export class StcPhoneInputComponent implements AfterViewInit, OnDestroy, OnChanges {
  public static COUNT = 0;
  @Input() public country?: string | null;
  @Input() public id?: string | null;
  @Input() public value?: PhoneMetadata | null;
  @Input() public label?: string | null;
  @Input() public placeholder?: string | null;
  @Input() public error?: string | null;
  @Input() public focusIndex?: number | null;
  @Input() public autofocus?: boolean | null;
  @Input() public disabled?: boolean | null;
  @Input() public required?: boolean | null;
  @Input() public loading?: boolean | null;
  @Input() public initialized?: boolean | null;

  @Output() public readonly phoneNumberChange: EventEmitter<PhoneMetadata | null>;
  @Output()
  public readonly debouncedPhoneNumberChange: EventEmitter<PhoneMetadata | null>;
  @Output() public readonly valueChange: EventEmitter<string>;
  @Output() public readonly debouncedValueChange: EventEmitter<string>;
  @Output() public readonly errorChange: EventEmitter<string | null>;
  @Output() public readonly statusChange: EventEmitter<string>;

  @ViewChild('field')
  private readonly popsyInputElement?: ElementRef | null;

  @ViewChild('countryCodeBtn')
  private readonly countryCodeBtnElement?: ElementRef | null;

  public rtlLayout$: Observable<boolean>;
  private userAction: boolean;

  private readonly debounceUserInputSubject: Subject<string>;
  private readonly debouncePhoneNumberInputSubject: Subject<PhoneMetadata | null>;
  private currentPhoneMetadata?: PhoneMetadata | null;

  public readonly generatedId: string;

  public focused: boolean;

  public autofilled?: boolean;

  public readonly formControl: UntypedFormControl;
  public readonly countryFormControl: UntypedFormControl;
  public readonly autocompleteCountryFormControl: UntypedFormControl;

  private readonly focusSubscription?: Subscription | null;
  private autofillSubscription?: Subscription | null;
  private countryOverlayObserver?: Subscription | null;
  private readonly formObserver: Subscription | null;
  private readonly subscriptions: Subscription;

  public showCountryPicker?: boolean | null;
  private allowPhoneValidation: boolean;

  private readonly generatedPlaceholderSubject: BehaviorSubject<string>;
  public readonly generatedPlaceholder$: Observable<string>;

  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly autofill: AutofillMonitor,
    private readonly translateService: TranslocoService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly phoneCountryOverlayDynamicService: PhoneCountryOverlayDynamicService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();
    this.generatedId = `stc-phone-input-${StcPhoneInputComponent.COUNT++}`;
    this.formControl = new UntypedFormControl('', [Validators.required]);
    this.countryFormControl = new UntypedFormControl('', [Validators.required]);
    this.autocompleteCountryFormControl = new UntypedFormControl('', {
      validators: Validators.compose([
        Validators.required,
        Validators.minLength(PHONE_COUNTRY_CODE_MIN_LENGTH),
        Validators.maxLength(PHONE_COUNTRY_CODE_MAX_LENGTH),
      ]),
      updateOn: 'change',
    });

    this.generatedPlaceholderSubject = new BehaviorSubject('');
    this.generatedPlaceholder$ = this.generatedPlaceholderSubject.asObservable();

    const phoneCountryMetadata = findPhoneCountryInfo(this.country || 'BR');
    this.countryFormControl.setValue(phoneCountryMetadata);
    this.autocompleteCountryFormControl.setValue(`+${phoneCountryMetadata?.phone || ''}`);

    this.userAction = false;
    this.focused = false;
    this.allowPhoneValidation = false;

    this.phoneNumberChange = new EventEmitter();
    this.debouncedPhoneNumberChange = new EventEmitter();
    this.valueChange = new EventEmitter();
    this.debouncedValueChange = new EventEmitter();
    this.errorChange = new EventEmitter();
    this.statusChange = new EventEmitter();

    this.rtlLayout$ = this.akitaRouterQuery.selectIsRtlLayout();

    this.debounceUserInputSubject = new Subject();
    this.subscriptions.add(
      this.debounceUserInputSubject
        .asObservable()
        .pipe(
          debounceTime(DEBOUNCE_USER_INPUT),
          distinctUntilChanged(),
          takeUntil(this.destroySubject)
        )
        .subscribe({
          next: (text: string) => {
            this.debouncedValueChange.next(text);
          },
        })
    );

    this.debouncePhoneNumberInputSubject = new Subject();
    this.subscriptions.add(
      this.debouncePhoneNumberInputSubject
        .asObservable()
        .pipe(
          tap((value: PhoneMetadata | null) => {
            this.currentPhoneMetadata = value || null;
          }),
          debounceTime(DEBOUNCE_USER_INPUT),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          takeUntil(this.destroySubject)
        )
        .subscribe({
          next: (value: PhoneMetadata | null) => {
            this.debouncedPhoneNumberChange.next(value);
          },
        })
    );

    this.formObserver = new Subscription();
    this.formObserver.add(
      this.formControl.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe({
        next: () => {
          // this.valueChange.next(`${value || ''}`);
          // this.debounceUserInputSubject.next(`${value || ''}`);
        },
      })
    );

    this.formObserver.add(
      this.autocompleteCountryFormControl.valueChanges
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (value: string) => {
            const parsedValue = `${value || ''}`.replace('+', '');
            const countryInfo =
              findPhoneCountryInfoWithCountryPrefix(parsedValue) || null;
            this.countryFormControl.setValue(countryInfo);
            this.formControl.setValue(
              this.formatPhone(this.formControl.value, countryInfo?.code)
            );

            try {
              const placeholderNumberNumber = getExampleNumber(
                `${countryInfo?.code || 'US'}`.toUpperCase() as CountryCode,
                examplePhoneNumbers
              );
              // remove starting 0
              let placeholderString = placeholderNumberNumber?.formatNational() || '';
              placeholderString =
                placeholderString[0] === '0'
                  ? placeholderString.slice(1)
                  : placeholderString;

              if (placeholderNumberNumber) {
                this.generatedPlaceholderSubject.next(placeholderString);
              } else {
                this.generatedPlaceholderSubject.next('');
              }
            } catch (err) {}

            if (parsedValue && !`${value || ''}`.includes('+')) {
              this.autocompleteCountryFormControl.setValue(`+${parsedValue}`, {
                emitEvent: false,
                onlySelf: true,
              });
            }
          },
        })
    );

    this.formObserver.add(
      this.formControl.statusChanges.pipe(takeUntil(this.destroySubject)).subscribe({
        next: (status: string) => {
          this.statusChange.next(`${status || ''}`);

          if (status === 'VALID') {
            this.valueChange.next(this.formControl.value);
            this.debounceUserInputSubject.next(this.formControl.value);

            if ((this.formControl.value || '').length >= MIN_LENGTH) {
              if (this.userAction) {
                const parsedPhone = popsyParseAnyPhone(
                  `${this.countryFormControl.value?.phone || ''}`,
                  `${this.formControl.value || ''}`,
                  `${
                    this.countryFormControl.value?.code || this.country || ''
                  }`.toUpperCase() as any
                );

                this.phoneNumberChange.next({
                  countryCode: parsedPhone?.countryCode || '',
                  areaCode: parsedPhone?.areaCode || '',
                  national: parsedPhone?.national || '',
                  display: parsedPhone?.display || '',
                });
                this.debouncePhoneNumberInputSubject.next({
                  countryCode: parsedPhone?.countryCode || '',
                  areaCode: parsedPhone?.areaCode || '',
                  national: parsedPhone?.national || '',
                  display: parsedPhone?.display || '',
                });
              }
            } else {
              if (this.userAction) {
                this.phoneNumberChange.next(null);
                this.debouncePhoneNumberInputSubject.next(null);
              }
            }
            this.errorChange.next(null);
            this.error = null;
            this.changeDetector.markForCheck();
          } else {
            if (this.userAction) {
              this.phoneNumberChange.next(null);
              this.debouncePhoneNumberInputSubject.next(null);
            }
            this.valueChange.next('');
            this.errorChange.next(this.parsePhoneError());
            this.debounceUserInputSubject.next('');
            this.error = this.parsePhoneError();
            this.changeDetector.markForCheck();
          }
        },
      })
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.autofocus && changes.autofocus.isFirstChange()) {
      if (this.autofocus && this.popsyInputElement?.nativeElement) {
        this.popsyInputElement.nativeElement.focus();
      }
    }

    if (changes?.country) {
      const phoneCountryMetadata = findPhoneCountryInfo(this.country || 'BR');
      if (
        JSON.stringify(this.countryFormControl.value) !==
        JSON.stringify(phoneCountryMetadata)
      ) {
        this.countryFormControl.setValue(phoneCountryMetadata);
      }

      const newCountryDisplay = `+${phoneCountryMetadata?.phone || ''}`;
      if (this.autocompleteCountryFormControl.value !== newCountryDisplay) {
        this.autocompleteCountryFormControl.setValue(newCountryDisplay);
      }
    }

    if (changes?.value && this.value) {
      if (
        (this.value.countryCode || this.value.areaCode || this.value.national) &&
        (this.currentPhoneMetadata?.countryCode !== this.value.countryCode ||
          this.currentPhoneMetadata?.areaCode !== this.value.areaCode ||
          this.currentPhoneMetadata?.national !== this.value.national)
      ) {
        const countryCode = `${this.value.countryCode || ''}`;
        const phoneCountryMetadata = findPhoneCountryInfoWithCountryPrefix(countryCode);
        if (countryCode) {
          if (
            JSON.stringify(this.countryFormControl.value) !==
            JSON.stringify(phoneCountryMetadata)
          ) {
            this.countryFormControl.setValue(phoneCountryMetadata);
          }

          const newCountryDisplay = `+${phoneCountryMetadata?.phone || ''}`;
          if (this.autocompleteCountryFormControl.value !== newCountryDisplay) {
            this.autocompleteCountryFormControl.setValue(newCountryDisplay);
          }
        }

        const phone = `${this.value.areaCode || ''}${this.value.national || ''}`;
        this.formControl.setValue(this.formatPhone(phone, phoneCountryMetadata?.code));
      }
    }
  }

  public ngAfterViewInit(): void {
    if (this.popsyInputElement) {
      if (this.autofocus && this.popsyInputElement?.nativeElement) {
        this.popsyInputElement.nativeElement.focus();
      }

      this.autofillSubscription = this.autofill
        .monitor(this.popsyInputElement)
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: ($event: AutofillEvent) => {
            this.autofilled = $event.isAutofilled;
            this.userAction = $event.isAutofilled;
          },
          error: () => {},
        });
    }
  }

  private parsePhoneError(): string | null {
    if (this.formControl.hasError('required')) {
      return this.translateService.translate('error_field_is_required', {
        var1: this.translateService.translate('hint_phone'),
      });
    } else if (this.formControl.hasError('invalid')) {
      return 'error_phone_not_valid';
    }
    return null;
  }

  public onFocus(): boolean {
    this.userAction = true;
    this.focused = true;
    this.allowPhoneValidation = false;
    this.changeDetector.markForCheck();
    return true;
  }

  public onBlur(): boolean {
    this.focused = false;
    this.allowPhoneValidation = true;

    if (this.akitaRouterQuery.isBrowser && this.popsyInputElement?.nativeElement) {
      const phone = this.popsyInputElement.nativeElement.value;
      const countryInfo = this.countryFormControl.value;
      this.formControl.setValue(this.formatPhone(phone, countryInfo?.code));
    }

    return true;
  }

  private formatPhone(text?: string | null, country?: string | null): string {
    const countryCode = (`${country || ''}`.toUpperCase() ||
      this.country ||
      'BR') as CountryCode;
    let formatAsYouType = new AsYouType(countryCode);

    let formatted = formatAsYouType.input(`${text || ''}`);
    const detectedCountry = formatAsYouType.getCountry() || countryCode;
    if (formatAsYouType.isInternational() && detectedCountry) {
      const phoneCountryMetadata = findPhoneCountryInfo(detectedCountry || countryCode);
      const replaceCode = `${phoneCountryMetadata?.phone || ''}`;

      if (phoneCountryMetadata?.code !== this.countryFormControl.value?.code) {
        this.countryFormControl.setValue(phoneCountryMetadata);
        this.autocompleteCountryFormControl.setValue(
          `+${phoneCountryMetadata?.phone || ''}`
        );
      }

      formatAsYouType = new AsYouType(detectedCountry);
      formatted = formatAsYouType.input(
        `${text || ''}`.replace(`+${replaceCode || ''}`, '')
      );
    }

    return formatted;
  }

  public phoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.allowPhoneValidation) {
        const phone = `${this.countryFormControl.value.phone} ${control.value || ''}`;
        if (popsyCheckPhone(phone, this.country as CountryCode)) {
          return null;
        }
        return {
          invalid: true,
        };
      }
      return null;
    };
  }

  public onChangedCountry($event?: PhoneCountryInfo | null): void {
    this.countryFormControl.setValue($event || null);
    this.autocompleteCountryFormControl.setValue(`+${$event?.phone || ''}`);
    this.formControl.setValue(this.formatPhone(this.formControl.value, $event?.code));
  }

  public onOpenCountryPicker(): boolean {
    if (this.countryOverlayObserver) {
      this.countryOverlayObserver.unsubscribe();
    }

    this.countryOverlayObserver = this.phoneCountryOverlayDynamicService.observe
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (data?: any | null) => {
          this.showCountryPicker = Boolean(data);
          this.changeDetector.markForCheck();

          if (
            !this.showCountryPicker &&
            this.phoneCountryOverlayDynamicService.selectedCountry
          ) {
            const countryInfo = this.phoneCountryOverlayDynamicService.selectedCountry;
            this.countryFormControl.setValue(countryInfo);
            this.autocompleteCountryFormControl.setValue(
              `+${this.phoneCountryOverlayDynamicService.selectedCountry?.phone || ''}`
            );
            this.formControl.setValue(
              this.formatPhone(this.formControl.value, countryInfo?.code)
            );
          }
        },
      });

    this.phoneCountryOverlayDynamicService.open({
      elementRef: this.countryCodeBtnElement,
      allowLowerPositions: true,
      backdropClass: 'popsy-white-opaque-backdrop',
      data: {
        allowedCountries: LIMIT_PHONE_COUNTRIES,
      },
      isBrowser: this.akitaRouterQuery.isBrowser,
    });
    return false;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    if (this.formObserver) {
      this.formObserver.unsubscribe();
    }

    if (this.autofillSubscription) {
      this.autofillSubscription.unsubscribe();
    }

    if (this.focusSubscription) {
      this.focusSubscription.unsubscribe();
    }
  }
}
